import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy">
      <div className="page-header">
        <div className="container">
          <h1 className="page-title fade-in">Privacy Policy</h1>
          <p className="page-subtitle fade-in">Last updated: March 2, 2026</p>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="content-wrapper card fade-in">
            <section className="content-section">
              <h2>1. Introduction</h2>
              <p>
                Welcome to EnglishMaster ("we," "our," or "us"). We are committed to protecting your 
                personal information and your right to privacy. This Privacy Policy explains how we 
                collect, use, disclose, and safeguard your information when you use our mobile application 
                and website (collectively, the "Service").
              </p>
              <p>
                Please read this privacy policy carefully. If you do not agree with the terms of this 
                privacy policy, please do not access the Service.
              </p>
            </section>

            <section className="content-section">
              <h2>2. Information We Collect</h2>
              <h3>2.1 Personal Information</h3>
              <p>
                We collect personal information that you voluntarily provide to us when you register on 
                the Service, express an interest in obtaining information about us or our products and 
                services, or otherwise contact us. The personal information we collect may include:
              </p>
              <ul>
                <li>Name and email address</li>
                <li>Username and password</li>
                <li>Profile information (age, language preferences, learning goals)</li>
                <li>Payment information (processed securely through third-party payment processors)</li>
                <li>Communications you send to us</li>
              </ul>

              <h3>2.2 Automatically Collected Information</h3>
              <p>
                When you use our Service, we automatically collect certain information about your device 
                and usage patterns, including:
              </p>
              <ul>
                <li>Device information (model, operating system, unique device identifiers)</li>
                <li>Log data (IP address, browser type, pages visited, time stamps)</li>
                <li>Usage data (lessons completed, time spent, performance metrics)</li>
                <li>Location data (with your permission)</li>
                <li>Analytics data through cookies and similar technologies</li>
              </ul>
            </section>

            <section className="content-section">
              <h2>3. How We Use Your Information</h2>
              <p>We use the information we collect or receive to:</p>
              <ul>
                <li>Provide, maintain, and improve our Service</li>
                <li>Create and manage your account</li>
                <li>Process your transactions and send related information</li>
                <li>Personalize your learning experience</li>
                <li>Track your progress and provide performance analytics</li>
                <li>Send you technical notices, updates, and support messages</li>
                <li>Respond to your comments, questions, and customer service requests</li>
                <li>Send you marketing and promotional communications (with your consent)</li>
                <li>Monitor and analyze trends, usage, and activities</li>
                <li>Detect, prevent, and address technical issues and security threats</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="content-section">
              <h2>4. Sharing Your Information</h2>
              <p>
                We may share your information in the following situations:
              </p>
              <ul>
                <li>
                  <strong>Service Providers:</strong> We may share your information with third-party 
                  vendors, service providers, contractors, or agents who perform services for us or on 
                  our behalf.
                </li>
                <li>
                  <strong>Business Transfers:</strong> We may share or transfer your information in 
                  connection with, or during negotiations of, any merger, sale of company assets, 
                  financing, or acquisition of all or a portion of our business.
                </li>
                <li>
                  <strong>Legal Requirements:</strong> We may disclose your information where we are 
                  legally required to do so in order to comply with applicable law, governmental requests, 
                  or legal process.
                </li>
                <li>
                  <strong>With Your Consent:</strong> We may disclose your personal information for any 
                  other purpose with your consent.
                </li>
              </ul>
              <p>
                We do not sell, rent, or trade your personal information to third parties for their 
                marketing purposes.
              </p>
            </section>

            <section className="content-section">
              <h2>5. Data Security</h2>
              <p>
                We implement appropriate technical and organizational security measures to protect your 
                personal information against unauthorized access, alteration, disclosure, or destruction. 
                These measures include:
              </p>
              <ul>
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security assessments and audits</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Employee training on data protection</li>
                <li>Incident response procedures</li>
              </ul>
              <p>
                However, no method of transmission over the Internet or electronic storage is 100% secure. 
                While we strive to use commercially acceptable means to protect your personal information, 
                we cannot guarantee absolute security.
              </p>
            </section>

            <section className="content-section">
              <h2>6. Data Retention</h2>
              <p>
                We retain your personal information only for as long as necessary to fulfill the purposes 
                outlined in this Privacy Policy, unless a longer retention period is required or permitted 
                by law. When we no longer need your information, we will securely delete or anonymize it.
              </p>
            </section>

            <section className="content-section">
              <h2>7. Your Privacy Rights</h2>
              <p>
                Depending on your location, you may have the following rights regarding your personal 
                information:
              </p>
              <ul>
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request transfer of your data to another service</li>
                <li><strong>Objection:</strong> Object to processing of your personal information</li>
                <li><strong>Restriction:</strong> Request restriction of processing</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
              </ul>
              <p>
                To exercise these rights, please contact us using the contact information provided below.
              </p>
            </section>

            <section className="content-section">
              <h2>8. Children's Privacy</h2>
              <p>
                Our Service is not directed to children under the age of 13. We do not knowingly collect 
                personal information from children under 13. If you are a parent or guardian and believe 
                your child has provided us with personal information, please contact us, and we will take 
                steps to delete such information from our systems.
              </p>
            </section>

            <section className="content-section">
              <h2>9. International Data Transfers</h2>
              <p>
                Your information may be transferred to and maintained on computers located outside of your 
                state, province, country, or other governmental jurisdiction where data protection laws may 
                differ. By using our Service, you consent to such transfers.
              </p>
            </section>

            <section className="content-section">
              <h2>10. Third-Party Links</h2>
              <p>
                Our Service may contain links to third-party websites or services. We are not responsible 
                for the privacy practices of these third parties. We encourage you to read their privacy 
                policies.
              </p>
            </section>

            <section className="content-section">
              <h2>11. Updates to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new Privacy Policy on this page and updating the "Last updated" date. We 
                encourage you to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section className="content-section">
              <h2>12. Contact Us</h2>
              <p>
                If you have questions or concerns about this Privacy Policy or our privacy practices, 
                please contact us at:
              </p>
              <div className="contact-info">
                <p><strong>Email:</strong> privacy@englishmaster.com</p>
                <p><strong>Phone:</strong> +1 (234) 567-890</p>
                <p><strong>Address:</strong> 123 Learning Street, Education City, EC 12345</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
